import { Theme } from '@mui/material'
import { CssUnit } from '../model/CssUnit'
import { FIELD_BORDER_RADIUS, FIELD_BORDER_WIDTH } from '../util/theme.constant'

// ===== Flexbox Utilities =====
export const flexCenterX = { display: 'flex', alignItems: 'center' }

export const flexCenterY = { display: 'flex', justifyContent: 'center' }

export const flexCenter = { ...flexCenterX, ...flexCenterY }

export const flexColumn = { display: 'flex', flexDirection: 'column' }

export const flexColumnCenterY = { ...flexColumn, alignItems: 'center' }

// ===== Size Utilities =====
export const normalSize = { minWidth: '0%', maxWidth: 1 }

export const fullSize = { width: 1, height: 1 }

export const fullScreen = { width: '100vw', height: '100vh' }

export const fullCoords = { left: 0, right: 0, bottom: 0, top: 0 }

export const absoluteFullSize = { ...fullSize, ...fullCoords, position: 'absolute' as const }

export const absoluteFullScreen = { ...fullScreen, ...fullCoords, position: 'absolute' as const }

// ===== Spacing Utilities =====
export const noIndents = { padding: 0, margin: 0 }

export const horizontalSpacing = (spacing: CssUnit) => ({ handleCssUnit }: Theme) => ({
  mx: handleCssUnit(spacing),
  '&:first-of-type': {
    ml: 0,
  },
  '&:last-of-type': {
    mr: 0,
  },
})

// ===== Typography Templates =====
export const title = {
  lineHeight: 1.28,
  fontWeight: 600,
}

export const normalText = {
  fontStyle: 'normal' as const,
  fontWeight: 'normal' as const,
}

export const text = ({ handleCssUnit }: Theme) => ({
  ...normalText,
  fontSize: handleCssUnit(18),
  lineHeight: 1.48,
})

// ===== Form & Clickable Element Templates =====
export const fieldInput = {
  ...flexCenter,
  ...normalSize,
  fontStyle: 'normal' as const,
  fontWeight: 500,
  letterSpacing: '0.02em',
  lineHeight: 1.5,
  position: 'relative' as const,
  border: 0,
  padding: '0.888em',
  borderRadius: `${FIELD_BORDER_RADIUS}px`,
  transition: 'all 0.2s ease-in-out',
}

export const baseClickable = (theme: Theme) => {
  return {
    fontWeight: 500,
    fontSize: theme.handleCssUnit(18),
    color: theme.palette.text.primary,
    padding: '0.888em',
    borderRadius: `${FIELD_BORDER_RADIUS}px`,
  }
}

export const baseOutlinedClickable = (theme: Theme) => ({
  ...fieldInput,
  ...baseClickable(theme),
  lineHeight: 1.3,
  borderStyle: 'solid' as const,
  borderWidth: `${FIELD_BORDER_WIDTH}px`,
  maxWidth: '100%',
  borderRadius: `${FIELD_BORDER_RADIUS}px`,
  paddingRight: '1.555em',
  paddingLeft: '1.555em',
  '&:hover': {
    textDecoration: 'none',
  },
  '&::placeholder': {
    color: theme.palette.grey['400'],
  },
  '&[type="number"]': {
    appearance: 'textfield' as const,
  },
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    appearance: 'none' as const,
  },
})

export const outlinedClickable = (theme: Theme) => ({
  ...baseOutlinedClickable(theme),
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.background.default,
  '&:focus, &:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderWidth: `${FIELD_BORDER_WIDTH}px`,
  },
})

// ===== Legacy Class-based Access (for gradual migration) =====
// @deprecated Use individual exports instead
export class AppStyle {
  static title = title
  static normalText = normalText
  static text = text
  static fieldInput = fieldInput
  static baseClickable = baseClickable
  static baseOutlinedClickable = baseOutlinedClickable
  static outlinedClickable = outlinedClickable
}

// @deprecated Use individual exports instead
export class SxHelperStyle {
  static normalSize = normalSize
  static fullSize = fullSize
  static fullScreen = fullScreen
  static fullCoords = fullCoords

  static get absoluteFullSize() {
    return absoluteFullSize
  }

  static get absoluteFullScreen() {
    return absoluteFullScreen
  }

  static flexCenterX = flexCenterX
  static flexCenterY = flexCenterY
  static flexCenter = flexCenter
  static flexColumn = flexColumn
  static flexColumnCenterY = flexColumnCenterY
  static noIndents = noIndents
  static horizontalSpacing = horizontalSpacing
}
